import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Field, Form } from 'formik';
import { Select, TextField } from 'formik-mui';
import * as Yup from 'yup';
import { useTheme } from '@emotion/react'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { visuallyHidden } from '@mui/utils';
import { useState } from 'react';
import { useEffect } from 'react';
import { useCallback } from 'react';
import { Button, MenuItem, Stack, Grid, TextField as MuiTextField, Chip } from '@mui/material';
import useAxiosPrivate from '../../hooks/use-axios-private';
import { alertMsg } from '../../utils/basicUtils';
import Empty from '../../components/Empty';
import ModalBox from '../../components/ModalBox';
import { Formik } from 'formik';
import Popup from '../../components/Popup';
import { COUPON_STATUS } from '../../constants';
import StrictNumberField from '../../components/StrictNumberField';
import { APP_CURRENCY } from '../../constants';

const getStatusColor = (status) => {
    switch (status) {
        case COUPON_STATUS.deactive:
            return 'warning'//yellow       
        case COUPON_STATUS.active:
            return 'success'//green        
        case COUPON_STATUS.expiry:
            return 'warning'
        default:
            return 'error'//red
    }
}

const getStatusName = (status) => {
    switch (status) {
        case COUPON_STATUS.deactive:
            return 'Deactive'//yellow        
        case COUPON_STATUS.active:
            return 'Active'//green        
        case COUPON_STATUS.expiry:
            return 'Expiry'
        default:
            return 'unknown'//red
    }
}

const headCells = [
    {
        id: 'id',
        numeric: false,
        disablePadding: true,
        label: 'Coupon ID',
    },
    {
        id: 'title',
        numeric: true,
        disablePadding: false,
        label: 'Coupon Code',
    },
    {
        id: 'coupontype',
        numeric: true,
        disablePadding: false,
        label: 'Coupon Type',
    },
    {
        id: 'status',
        numeric: true,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'limit',
        numeric: true,
        disablePadding: false,
        label: 'Used Limit',
    },
    {
        id: 'min_spend',
        numeric: true,
        disablePadding: false,
        label: `Min Spend (${APP_CURRENCY.currency_symbol}) `,
    },
    {
        id: 'expire_on',
        numeric: true,
        disablePadding: false,
        label: 'Expires On',
    },
    {
        id: 'actions',
        numeric: true,
        disablePadding: false,
        label: 'Actions',
    },

];

const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort } = props;

    const createSortHandler = (newOrderBy) => {

        onRequestSort(newOrderBy);
    };

    return (
        <TableHead>
            <TableRow sx={{ height: { xs: 65, xl: 75 } }}>
                {/* <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts',
                        }}
                    />
                </TableCell> */}
                {headCells.map((headCell, index) => (
                    <TableCell
                        key={index}
                        align={'center'}
                        // padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {
                            headCell.id !== 'action'
                                ?
                                <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy}
                                    onClick={() => createSortHandler(headCell.id)}
                                >
                                    {headCell.label}
                                    {orderBy === headCell.id && (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    )
                                    }
                                </TableSortLabel>
                                :
                                headCell.label
                        }
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    // onSelectAllClick: PropTypes.func.isRequired,
    //  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    // orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
    const { numSelected, onSearchChange } = props
    const theme = useTheme()

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
                alignItems: { xs: 'end', sm: 'center' }
            }}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'baseline', sm: 'center' }} justifyContent='space-between' sx={{ width: '100%', mt: { xs: 3, sm: 0 } }}>
                    <Typography
                        sx={{ flex: '1 1 100%', color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >
                        Coupon List
                    </Typography>
                    <Box sx={{ mr: 3, width: { xs: '100%', sm: 300 }, maxWidth: '100%', }}>
                        <MuiTextField fullWidth label="Search" variant="standard" size="small" type='search' onChange={onSearchChange} autoComplete='off' />
                    </Box>
                </Stack>
            )}

            {numSelected > 0 && (
                <>
                    <Tooltip title="Delete">
                        <IconButton color='error'>
                            <DeleteOutlineOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                        <IconButton>
                            <ContentCopyOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton>
                            <EditOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}        </Toolbar>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};




const Coupon = () => {
    const theme = useTheme()
    const axios = useAxiosPrivate()
    const [ascorder, setAscorder] = useState("desc")
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [coupons, setCoupons] = useState([]);
    const COUPONINFORMATIONVALUES =
    {
        "coupon_code": "",
        "coupon_discount_type": "",
        "expiry_date": "",
        "coupon_limit": "",
        "alter_limit": "",
        "limit_per_user": "",
        "coupon_amount": "",
        "coupon_percentage": "",
        "coupon_min_amount": ""

    }
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [paddingHeight, setPaddingHeight] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [couponCount, setCouponCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [couponOperation, setCouponOperation] = useState('Create')
    const [couponValues, setCouponValues] = useState(COUPONINFORMATIONVALUES)
    const [percentage, setPercentage] = useState('')
    const [orderByCoupon, setOrderByCoupon] = useState([])
    const [showAlert, setShowAlert] = useState(false);
    const [couponAlert, setCouponAlert] = useState(false)

    const createCouponValidation = Yup.object({
        coupon_code: Yup.string()
            .required('Coupon Code is required')
            .min(3, 'Coupon Code must be at least 3 characters')
            .max(45, 'Coupon Code cannot exceed 45 characters'),
        coupon_percentage: Yup.number()
            .min(0.1, "Please enter the value greater than 0")
            .max(100, 'Please enter a value less than or equal to 100'),

        coupon_discount_type:
            Yup.string()
                .required('Coupon Type is required'),
        coupon_limit: Yup.number()
            .required('coupon limit is required')
            .max(5000, 'Coupon name must not exceed more than 5 digits')
            .integer("Enter the value must be an integer"),
        limit_per_user: Yup.number()
            .required('User limit is required')
            .positive()
            .lessThan(Yup.ref('coupon_limit'), 'Please enter a value less than to Coupon limit')
            .integer("Enter the value must be an integer"),
    })
    const getCouponList = async () => {
        try {
            const resp = await axios.get('coupon/', {
                params: {
                    limit: rowsPerPage, search: searchText, page: page + 1,
                }
            })
            setCoupons(resp.data.data.coupons)
            setCouponCount(resp.data.data.page_info.count)
        }
        catch (err) {
            console.error(err)
        }
    }

    const deleteCoupon = async (id) => {
        try {
            let resp = await axios.delete(`coupon/${id}`)
            getCouponList()
            alertMsg(resp.data.message, 'success')
        } catch (e) {
            console.error(e)
            alertMsg('Failed to delete', 'error')
        }
    }

    const couponStatus = async (id) => {
        try {
            let resp = await axios.post(`coupon/change_status/`, { coupon_id: id })
            getCouponList()
            alertMsg(resp.data.message, 'success')
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        getCouponList()
    }, [rowsPerPage, searchText, page, ascorder, orderByCoupon]);


    const orderChange = (newOrderBy) => {
        if (newOrderBy === 'action') {
            return false
        }
        if (ascorder === "desc") {
            setAscorder("asc")
            setOrderByCoupon(newOrderBy)
        }
        else {
            setAscorder("desc")
            setOrderByCoupon(newOrderBy)
        }
    }

    const handleChangePage = useCallback(
        (event, newPage) => {
            setPage(newPage)
        },
        []
    );

    const handleChangeRowsPerPage = useCallback(
        (event) => {
            const updatedRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(updatedRowsPerPage);
            setPage(0);
            // There is no layout jump to handle on the first page.
            setPaddingHeight(0);
        }, []
    );

    const isSelected = order_id => selected.indexOf(order_id) !== -1;
    return (

        <>
            <Box sx={{ textAlign: "right", mb: 2 }}>
                <Button variant='outlined'
                    onClick={() => {
                        setOpen(true)
                        setCouponOperation('Create')
                        setCouponValues(COUPONINFORMATIONVALUES)
                    }}
                >Create New</Button>
            </Box>
            <Box sx={{ width: '100%', mt: 1 }}>
                <Paper sx={{ width: '100%', mb: 2, boxShadow: "2px 2px 12px 0px rgb(0 0 0 / 11%)" }}>
                    <Box>
                        <EnhancedTableToolbar
                            numSelected={selected.length}
                            onSearchChange={
                                (e) => {
                                    setSearchText(e.target.value)
                                }
                            }
                        />

                        {coupons.length > 0
                            ?
                            <>
                                <TableContainer>
                                    <Table
                                        sx={{ minWidth: 750 }}
                                        aria-labelledby="tableTitle"
                                        size={'small'}
                                    >
                                        <EnhancedTableHead
                                            numSelected={selected.length}
                                            onRequestSort={(newOrderBy) => { orderChange(newOrderBy) }}
                                            rowCount={coupons.length}
                                            orderBy={ascorder}
                                        />
                                        <TableBody>
                                            {coupons
                                                ? coupons.map((coupon, index) => {
                                                    const isItemSelected = isSelected(coupon.id);
                                                    const labelId = `enhanced-table-checkbox-${index}`;
                                                    return (
                                                        <TableRow
                                                            hover
                                                            // onClick={() => navigate(`/coupon/${coupon.id}`)}                                                            role="checkbox"
                                                            aria-checked={isItemSelected}
                                                            tabIndex={-1}
                                                            key={coupon.id}
                                                            selected={isItemSelected}
                                                            sx={{ cursor: 'pointer', height: { xs: 65, md: 75 } }}
                                                            onClick={() => {
                                                                setOpen(true)
                                                                setCouponValues(coupon)
                                                                setCouponOperation('Update')
                                                            }}
                                                        >
                                                            <TableCell
                                                                component="th"
                                                                id={labelId}
                                                                scope="row"
                                                                align="center"
                                                                sx={{ whiteSpace: "nowrap" }}
                                                            >
                                                                {coupon.id}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{coupon.coupon_code}</TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{coupon.coupon_discount_type === 1 ? "Percentage" : "Price"}</TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                                                <Chip sx={{ textTransform: "capitalize" }} label={getStatusName(coupon.coupon_status)} color={getStatusColor(coupon.coupon_status)}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        // setOpenStatus(true);
                                                                        setCouponValues(coupon);
                                                                        setCouponAlert(true);
                                                                    }}>
                                                                </Chip>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{coupon.alter_limit}/{coupon.coupon_limit}</TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{APP_CURRENCY.currency_symbol} {coupon.coupon_min_amount}</TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{coupon.expiry_date}</TableCell>
                                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                                                <IconButton sx={{}} color="primary" component="span">
                                                                    <EditOutlinedIcon />
                                                                </IconButton>
                                                                <IconButton sx={{ ml: 1 }} color='error' onClick={(e) => { e.stopPropagation(); setCouponValues(coupon); setShowAlert(true); }} component="span">
                                                                    <DeleteOutlineOutlinedIcon />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                                : null}
                                            {paddingHeight > 0 && (
                                                <TableRow
                                                    style={{
                                                        height: paddingHeight,
                                                    }}
                                                >
                                                    <TableCell colSpan={6} />
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={couponCount}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                            :
                            <Empty sx={{ border: "none", }} icon={<ManageAccountsOutlinedIcon sx={{ fontSize: 40, color: "grey" }} />} text="No coupon available" />
                        }
                    </Box>
                </Paper>
            </Box>

            <Popup show={showAlert} onSecondaryClick={() => setShowAlert(false)} title={"Delete Services?"} consent={true} variant='error' primaryBtnTxt={"Delete"}
                onPrimaryClick={async (e, loader) => {
                    if (e.detail !== 1) {
                        return false
                    }
                    try {
                        loader(true)
                        await deleteCoupon(couponValues.id)
                        setShowAlert(false)
                        loader(false)
                    }
                    catch (error) {
                        console.error(error)
                    }
                }}

            >
                <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography variant='span' sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "400", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}>Are you sure you want to delete</Typography>
                    <Typography variant='span' sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "500", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}> {couponValues.coupon_code} </Typography>
                    <Typography variant='span' sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "400", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}>Coupon?</Typography>
                </Box>
                <Box sx={{ marginTop: "6px", textAlign: "center" }}>
                    <Typography sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "400", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}>You can't undo this action.</Typography>
                </Box>
            </Popup>
            <Popup show={couponAlert} onSecondaryClick={() => setCouponAlert(false)} title={couponValues.is_active === false ? "Do you want to Deactivate" : "Do you want to Activate"} consent={false} variant='primary' primaryBtnTxt={"Ok"}
                onPrimaryClick={async (e, loader) => {
                    if (e.detail !== 1) {
                        return false
                    }
                    try {
                        loader(true)
                        await couponStatus(couponValues.id)
                        setCouponAlert(false)
                        loader(false)
                    }
                    catch (error) {
                        console.error(error)
                    }
                }}

            >
                <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography variant='span' sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "400", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}>Are you sure do you want to</Typography>
                    <Typography variant='span' sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "500", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}> {couponValues.coupon_code} </Typography>
                    <Typography variant='span' sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "400", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}>Coupon?</Typography>
                </Box>
                <Box sx={{ marginTop: "6px", textAlign: "center" }}>
                    <Typography sx={{ fontSize: { xs: "16px", xl: "18px" }, fontWeight: "400", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}>You can't undo this action.</Typography>
                </Box>
            </Popup>


            <ModalBox
                open={open}
                onClose={() => { setOpen(false) }}
            >

                <Box sx={{ height: { xs: "80vh", sm: 'auto' }, overflow: 'auto', pr: 2 }} className="overflow-scroll">
                    <Box>
                        <Box mb={1}>
                            <Typography sx={{ fontSize: { xs: 20, xl: 22 }, color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}
                                onClick={() => {
                                    console.log(percentage)
                                }}
                            >{couponOperation} Coupon </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: { xs: "13px", sm: "15px" }, color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}> Here you can Create new coupon. </Typography>
                        </Box>
                    </Box>
                    <Formik
                        initialValues={couponValues}
                        onSubmit={async (values, { setSubmitting }) => {
                            let resp = false
                            if (couponOperation === 'Update') {
                                try {
                                    resp = await axios.put(`coupon/${values.id}`, { ...values })
                                } catch (e) {
                                    console.error(e)
                                    alertMsg('Failed to update coupon address', 'error')
                                    return
                                }
                            } else {
                                try {
                                    resp = await axios.post('coupon/', { ...values })

                                } catch (e) {
                                    console.error(e)
                                    alertMsg('Failed to coupon address', 'error')
                                    return
                                }
                            }
                            setSubmitting(false);
                            getCouponList()
                            alertMsg(resp.data.message, 'success')
                            setOpen(false)

                        }}
                        validationSchema={createCouponValidation}
                    >
                        {({ submitForm, isSubmitting, values }) => (
                            <Form>
                                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ pt: 2, }}>
                                    <Grid item xs={12} sm={6}>
                                        <Box>
                                            <Field
                                                component={TextField}
                                                name="coupon_code"
                                                label="Coupon Code *"
                                                inputProps={{ maxLength: 45 }}
                                                variant="outlined"
                                                disabled={couponOperation === "Update" ? true : false}
                                                fullWidth
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} >
                                        <Box>
                                            <Field
                                                sx={{}}
                                                component={Select}
                                                formControl={{ fullWidth: true }}
                                                name="coupon_discount_type"
                                                label="Select Coupon Type"
                                                disabled={couponOperation === "Update" ? true : false}
                                            >
                                                <MenuItem value="">Select</MenuItem>
                                                <MenuItem value="1">Percentage</MenuItem>
                                                <MenuItem value='2' >Price</MenuItem>
                                            </Field>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ display: couponOperation !== "Update" ? values.coupon_discount_type === '1' ? "block" : "none" : couponValues.coupon_discount_type === 1 ? "block" : "none" }}>
                                        <Box>
                                            <StrictNumberField name={couponOperation === "Update" ? "coupon_amount" : "coupon_percentage"} autoComplete='off'
                                                label="Enter the percentage" disabled={couponOperation === "Update" ? true : false} component={TextField} type="number" fullWidth />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ display: couponOperation !== "Update" ? values.coupon_discount_type === '2' ? "block" : "none" : couponValues.coupon_discount_type === 1 ? "none" : "block" }} >
                                        <Box>
                                            <StrictNumberField name="coupon_amount" label="Enter the price" autoComplete='off'
                                                disabled={couponOperation === "Update" ? true : false} component={TextField} type="number" fullWidth />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} >
                                        <Box>
                                            <StrictNumberField name="coupon_min_amount" label="Minimum Cart Value" disabled={couponOperation === "Update" ? true : false}
                                                autoComplete='off' inputProps={{ maxLength: 4 }} component={TextField} type="number" fullWidth />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} >
                                        <Box>
                                            <Field
                                                className="date"
                                                component={TextField}
                                                name="expiry_date"
                                                // label="Coupon Expiry Date"
                                                inputProps={{ min: new Date().toISOString().split("T")[0] }}
                                                data-date-inline-picker="true"
                                                variant="outlined"
                                                type="date"
                                                fullWidth
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{ pt: 4, }}>
                                    <Box>
                                        <Box mb={1}>
                                            <Typography sx={{ fontSize: { xs: "18px", md: "22px" }, color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}> Usage Limits </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: { xs: "13px", sm: "15px" }, color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }}> Here you can set limit for coupon. </Typography>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={{ xs: 2, md: 3 }} sx={{ pt: 2, }}>
                                        <Grid item xs={12} sm={6} >
                                            <Box>
                                                <StrictNumberField name="coupon_limit" label="Enter the coupon limit *" autoComplete='off' component={TextField} type="number" fullWidth />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} >
                                            <Box>
                                                <StrictNumberField name="limit_per_user" label="Enter the user limit *" autoComplete='off' component={TextField} type="number" fullWidth />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box sx={{ mt: 4, display: "flex", justifyContent: "end" }}>
                                    <Box>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            sx={{ mr: 2 }}
                                            onClick={() => {
                                                setOpen(false)
                                                setPercentage('')
                                            }
                                            }
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                    <Box>
                                        <Button
                                            variant="contained"
                                            disabled={isSubmitting}
                                            onClick={submitForm}
                                        >Save</Button>
                                    </Box>
                                </Box>

                            </Form>)}
                    </Formik>
                </Box>

            </ModalBox>
            {/* <ModalBox
                open={openstatus}
                onClose={() => { setOpenStatus(false) }}>
                <Box>
                    <Typography variant='span'>Are you sure do you want to</Typography>
                    <Typography variant='span' sx={{ fontSize: "18px", fontWeight: "500", marginLeft: "5px", marginRight: "5px" }}>{couponValues.coupon_code}</Typography>
                    <Typography variant='span'>coupon?</Typography>
                </Box>

                <Box sx={{ mt: 4, display: "flex", justifyContent: "end" }}>
                    <Box>
                        <Button
                            variant="outlined"
                            size="large"
                            color="error"
                            sx={{ mr: 2 }}
                            onClick={() => {
                                setOpenStatus(false)
                            }
                            }
                        >
                            Cancel
                        </Button>
                    </Box>
                    <Box>
                        <Button
                            size="large"
                            variant="outlined"
                        >Ok</Button>
                    </Box>
                </Box>
            </ModalBox> */}
        </>

    );
}

export default Coupon